import type { LoaderFunctionArgs } from '~/types/route-args';
import type { ShouldRevalidateFunction } from 'react-router';
import { data as routerData } from 'react-router';

import {
    type GetFeaturedProjectsQuery,
    type GetFeaturedProjectsQueryVariables,
} from 'generated/graphql';
import { GetFeaturedProjectsDocument } from 'generated/documents/GetFeaturedProjectsDocument';
import { mapProjectToTileViewModel } from '~/modules-rocco/projects/data-mappers/tile-project';
import { FEATURED_PROJECTS_PAGE_SIZE } from '~/modules/product/util/featured-projects-config';

export const LOAD_MORE_FEATURED_PROJECTS_ACTION =
    '/actions/load-more-featured-projects';

/** Cap per request to prevent resource exhaustion / abuse. */
const MAX_LIMIT = 50;

/** Only `/product/{segment}` links are valid (no traversal, no other routes). */
const PRODUCT_LINK_PATTERN = /^\/product\/[\w-]+$/;

const EMPTY = { projects: [], hasMore: false, totalCount: 0 };

/**
 * Fetches a later page of a product's Featured Projects for the revamp's
 * Tagged Projects "Load More". The loader SSRs page 0; this returns the mapped
 * tiles for `offset`/`limit` plus the connection's `hasNextPage` / `totalCount`.
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);
    const link = url.searchParams.get('link');
    const offsetParam = url.searchParams.get('offset');
    const limitParam = url.searchParams.get('limit');

    if (!link || link.includes('..') || !PRODUCT_LINK_PATTERN.test(link)) {
        logger.warning('Invalid link in load-more-featured-projects', { link });
        return EMPTY;
    }

    const offset = offsetParam ? parseInt(offsetParam, 10) : 0;
    if (!Number.isInteger(offset) || offset < 0) {
        logger.warning('Invalid offset in load-more-featured-projects', {
            offsetParam,
        });
        return EMPTY;
    }

    const limit = limitParam
        ? parseInt(limitParam, 10)
        : FEATURED_PROJECTS_PAGE_SIZE;
    if (!Number.isInteger(limit) || limit < 1 || limit > MAX_LIMIT) {
        logger.warning('Invalid limit in load-more-featured-projects', {
            limitParam,
        });
        return EMPTY;
    }

    try {
        const result = await context.client.query<
            GetFeaturedProjectsQuery,
            GetFeaturedProjectsQueryVariables
        >({
            query: GetFeaturedProjectsDocument,
            variables: { link, limit, offset },
        });

        const product = result.data?.Directory?.Product;
        if (!product || product.__typename !== 'Product') {
            return EMPTY;
        }

        const projects = product.FeaturedProjects.edges.map((edge) =>
            mapProjectToTileViewModel(edge.node, {
                appDisplayMode: context.appDisplayMode,
                imageToDisplay: 'featured',
            }),
        );

        return {
            projects,
            hasMore: product.FeaturedProjects.pageInfo.hasNextPage,
            totalCount: product.FeaturedProjects.pageInfo.totalCount,
        };
    } catch (error) {
        logger.error('Error in /actions/load-more-featured-projects', {
            error,
            link,
            offset,
            limit,
        });
        return routerData(EMPTY, { status: 500 });
    }
};

/** Data is fetched on demand; never revalidate on navigation. */
export const shouldRevalidate: ShouldRevalidateFunction = () => false;
